import {
  FieldDataType,
  FieldTypes,
} from "pages/employer-of-record/components/AutoForm";
import {
  addValidation,
  addOptions,
  addConfig,
} from "../../../components/AutoForm/field-utils";
import { PaymentInfoAutoFormFields } from "./payment-info-autoform-fields";
import {
  BankAccountTypeOption,
  BankAccountTypeOptions,
} from "./bankAccountTypeOptions";

const IBANAccountFormFields = (accountTypeOptions: BankAccountTypeOption[]) => [
  PaymentInfoAutoFormFields["iban"],
  addOptions(PaymentInfoAutoFormFields["accountType"], accountTypeOptions),
  PaymentInfoAutoFormFields["accountNickname"],
];

export const PaymentInfoAutoFormConfig: Record<string, FieldDataType[]> = {
  AU: [
    PaymentInfoAutoFormFields["bankName"],
    PaymentInfoAutoFormFields["bsbNumber"],
    PaymentInfoAutoFormFields["accountHolderName"],
    addValidation(PaymentInfoAutoFormFields["accountNumber"], {
      text: {
        // t("validations.text.max.message")
        max: { value: 9, message: "validations.text.max.message" },
        // t("validations.text.min.message")
        min: { value: 4, message: "validations.text.min.message" },
      },
    }),
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.AU
    ),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  BR: [
    addValidation(PaymentInfoAutoFormFields["bankCode"], {
      text: {
        length: {
          value: 3,
          // t("validations.text.length.message")
          message: "validations.text.length.message",
        },
      },
    }),
    addValidation(PaymentInfoAutoFormFields["branchCode"], {
      text: {
        // t("validations.text.max.message")
        max: { value: 4, message: "validations.text.max.message" },
        // t("validations.text.min.message")
        min: { value: 4, message: "validations.text.min.message" },
      },
    }),
    addConfig(PaymentInfoAutoFormFields["accountNumber"], {
      message:
        "Enter the account number followed by a hyphen and the account digit (e.g. 123456-7)",
      validations: {
        required: {
          enabled: true,
          message: "validations.required.message",
        },
        text: {
          number: {
            enabled: false,
            message: "validations.text.number.message",
          },
          // t("validations.text.max.message")
          max: { value: 13, message: "validations.text.max.message" },
          // t("validations.text.min.message")
          min: { value: 6, message: "validations.text.min.message" },
          matches: {
            value: /^[0-9]+-[0-9]{1,2}$/,
            // t("validations.accountNumber.br.matches.message")
            message: "validations.accountNumber.br.matches.message",
          },
        },
      },
    }),
    PaymentInfoAutoFormFields["accountHolderName"],
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.BR
    ),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  CA: [
    PaymentInfoAutoFormFields["institutionNumber"],
    PaymentInfoAutoFormFields["transitNumber"],
    addValidation(PaymentInfoAutoFormFields["accountNumber"], {
      text: {
        // t("validations.text.max.message")
        max: { value: 12, message: "validations.text.max.message" },
        // t("validations.text.min.message")
        min: { value: 6, message: "validations.text.min.message" },
      },
    }),
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.CA
    ),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  CL: [
    addValidation(PaymentInfoAutoFormFields["accountNumber"], {
      text: {
        // t("validations.text.max.message")
        max: { value: 20, message: "validations.text.max.message" },
        // t("validations.text.min.message")
        min: { value: 1, message: "validations.text.min.message" },
      },
    }),
    PaymentInfoAutoFormFields["bankName"],
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.CL
    ),
  ],
  CO: [
    PaymentInfoAutoFormFields["accountNumber"],
    PaymentInfoAutoFormFields["bankName"],
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.CO
    ),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  CR: [
    addValidation(PaymentInfoAutoFormFields["accountNumber"], {
      text: {
        // t("validations.text.max.message")
        max: { value: 14, message: "validations.text.max.message" },
        // t("validations.text.min.message")
        min: { value: 1, message: "validations.text.min.message" },
      },
    }),
    PaymentInfoAutoFormFields["bankName"],
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.CR
    ),
    PaymentInfoAutoFormFields["iban"],
  ],
  DE: IBANAccountFormFields(BankAccountTypeOptions.DE),
  DK: [
    addValidation(PaymentInfoAutoFormFields["accountNumber"], {
      text: {
        // t("validations.text.max.message")
        max: { value: 10, message: "validations.text.max.message" },
        // t("validations.text.min.message")
        min: { value: 1, message: "validations.text.min.message" },
      },
    }),
    addValidation(PaymentInfoAutoFormFields["registrationNumber"], {
      text: {
        // t("validations.text.max.message")
        max: { value: 4, message: "validations.text.max.message" },
        // t("validations.text.min.message")
        min: { value: 1, message: "validations.text.min.message" },
      },
    }),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  EE: IBANAccountFormFields(BankAccountTypeOptions.EE),
  ES: IBANAccountFormFields(BankAccountTypeOptions.ES),
  FR: IBANAccountFormFields(BankAccountTypeOptions.FR),
  GB: [
    PaymentInfoAutoFormFields["sortCode"],
    addValidation(PaymentInfoAutoFormFields["accountNumber"], {
      text: {
        max: { value: 8, message: "validations.text.max.message" },
      },
    }),
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.GB
    ),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  GR: [
    PaymentInfoAutoFormFields["bankName"],
    ...IBANAccountFormFields(BankAccountTypeOptions.GR),
  ],
  HR: [
    PaymentInfoAutoFormFields["bankName"],
    ...IBANAccountFormFields(BankAccountTypeOptions.HR),
  ],
  IE: IBANAccountFormFields(BankAccountTypeOptions.IE),
  IL: [
    addValidation(PaymentInfoAutoFormFields["bankCode"], {
      text: {
        length: {
          value: 3,
          message: "validations.text.length.message",
        },
        number: {
          enabled: true,
          message: "validations.text.number.message",
        },
      },
    }),
    addValidation(PaymentInfoAutoFormFields["branchCode"], {
      text: {
        max: { value: 4, message: "validations.text.max.message" },
        min: { value: 3, message: "validations.text.min.message" },
        number: {
          enabled: true,
          message: "validations.text.number.message",
        },
      },
    }),
    addValidation(PaymentInfoAutoFormFields["accountNumber"], {
      text: {
        max: { value: 9, message: "validations.text.max.message" },
        min: { value: 6, message: "validations.text.min.message" },
      },
    }),
    PaymentInfoAutoFormFields["bankCertificate"],
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.IL
    ),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  IN: [
    PaymentInfoAutoFormFields["ifscCode"],
    PaymentInfoAutoFormFields["accountNumber"],
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.IN
    ),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  IT: IBANAccountFormFields(BankAccountTypeOptions.IT),
  LV: [
    PaymentInfoAutoFormFields["bankName"],
    addConfig(PaymentInfoAutoFormFields["swiftCode"], {
      label: "BIC",
    }),
    ...IBANAccountFormFields(BankAccountTypeOptions.LV),
  ],
  MX: [
    PaymentInfoAutoFormFields["bankName"],
    PaymentInfoAutoFormFields["clabe"],
    PaymentInfoAutoFormFields["accountNumber"],
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.MX
    ),
    PaymentInfoAutoFormFields["accountNickname"],
    addConfig(PaymentInfoAutoFormFields["bankCertificate"], {
      label: "Bank Account Statement",
    }),
  ],
  MY: [
    PaymentInfoAutoFormFields["bankName"],
    PaymentInfoAutoFormFields["branchCode"],
    addValidation(PaymentInfoAutoFormFields["accountNumber"], {
      text: {
        // t("validations.text.max.message")
        max: { value: 20, message: "validations.text.max.message" },
        // t("validations.text.min.message")
        min: { value: 6, message: "validations.text.min.message" },
      },
    }),
    PaymentInfoAutoFormFields["accountNickname"],
    addConfig(PaymentInfoAutoFormFields["bankCertificate"], {
      label: "Bank Account Statement",
    }),
  ],
  NG: [
    PaymentInfoAutoFormFields["bankName"],
    PaymentInfoAutoFormFields["accountHolderName"],
    addConfig(PaymentInfoAutoFormFields["accountNumber"], {
      label: "Bank Account Number (NUBAN)",
    }),
    addConfig(PaymentInfoAutoFormFields["sortCode"], {
      validations: {
        required: {
          enabled: false,
          message: "validations.required.message",
        },
        text: {
          sortCode: {
            enabled: false,
            message: "validations.text.sortCode.message",
          },
        },
      },
      compact: (value: string) => value.replace(/\D/g, ""),
      format: (value: string) => value.replace(/\D/g, ""),
    }),
    PaymentInfoAutoFormFields["bvn"],
    addConfig(PaymentInfoAutoFormFields["swiftCode"], {
      validations: {
        required: {
          enabled: false,
          message: "validations.required.message",
        },
      },
    }),
  ],
  NL: IBANAccountFormFields(BankAccountTypeOptions.NL),
  NZ: [
    PaymentInfoAutoFormFields["bankName"],
    addConfig(PaymentInfoAutoFormFields["accountNumber"], {
      label: "Bank Account",
      message: "Enter the account number in format: AA-BBBB-CCCCCCCC-DDD",
      validations: {
        required: {
          enabled: true,
          message: "validations.required.message",
        },
        text: {
          number: {
            enabled: false,
            message: "validations.text.number.message",
          },
          matches: {
            value: /^\d{2}-\d{4}-\d{8}-\d{3}$/,
            message:
              "Must follow standard New Zealand bank account format: AA-BBBB-CCCCCCCC-DDD",
          },
        },
      },
    }),
    addConfig(PaymentInfoAutoFormFields["irdNumber"], {
      message:
        "If your IRD number is only 8 digits, please add a 0 to the front.",
    }),
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.NZ
    ),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  PA: [
    PaymentInfoAutoFormFields["accountNumber"],
    PaymentInfoAutoFormFields["bankName"],
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.PA
    ),
  ],
  PE: [
    PaymentInfoAutoFormFields["bankName"],
    addValidation(PaymentInfoAutoFormFields["accountNumber"], {
      text: {
        // t("validations.text.max.message")
        max: { value: 20, message: "validations.text.max.message" },
        // t("validations.text.min.message")
        min: { value: 1, message: "validations.text.min.message" },
      },
    }),
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.PE
    ),
    PaymentInfoAutoFormFields["accountNickname"],
    PaymentInfoAutoFormFields["ctsFinancialEntity"],
    PaymentInfoAutoFormFields["ctsAccountNumber"],
  ],
  PH: [
    PaymentInfoAutoFormFields["bankName"],
    PaymentInfoAutoFormFields["swiftCode"],
    PaymentInfoAutoFormFields["accountHolderName"],
    addValidation(PaymentInfoAutoFormFields["accountNumber"], {
      text: {
        // t("validations.text.max.message")
        max: { value: 17, message: "validations.text.max.message" },
      },
    }),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  PL: IBANAccountFormFields(BankAccountTypeOptions.PL),
  PT: IBANAccountFormFields(BankAccountTypeOptions.PT),
  RO: [
    PaymentInfoAutoFormFields["bankName"],
    ...IBANAccountFormFields(BankAccountTypeOptions.RO),
  ],
  SE: [
    PaymentInfoAutoFormFields["swiftCode"],
    addValidation(PaymentInfoAutoFormFields["accountNumber"], {
      text: {
        // t("validations.text.max.message")
        max: { value: 11, message: "validations.text.max.message" },
        // t("validations.text.min.message")
      },
    }),
    PaymentInfoAutoFormFields["clearingNumber"],
    PaymentInfoAutoFormFields["iban"],
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  SG: [
    PaymentInfoAutoFormFields["bankName"],
    PaymentInfoAutoFormFields["swiftCode"],
    PaymentInfoAutoFormFields["accountHolderName"],
    addValidation(PaymentInfoAutoFormFields["accountNumber"], {
      text: {
        // t("validations.text.max.message")
        max: { value: 19, message: "validations.text.max.message" },
        // t("validations.text.min.message")
        min: { value: 6, message: "validations.text.min.message" },
      },
    }),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  TR: [
    addValidation(PaymentInfoAutoFormFields["bankCode"], {
      text: {
        number: {
          enabled: true,
          message: "validations.text.number.message",
        },
        length: {
          value: 3,
          // t("validations.text.length.message")
          message: "validations.text.length.message",
        },
      },
    }),
    addValidation(PaymentInfoAutoFormFields["branchCode"], {
      text: {
        number: {
          enabled: true,
          message: "validations.text.number.message",
        },
        length: {
          value: 5,
          // t("validations.text.length.message")
          message: "validations.text.length.message",
        },
      },
    }),
    {
      type: FieldTypes.text,
      name: "accountNumber",
      // t("Account Number")
      label: "Account Number",
      validations: {
        required: {
          enabled: true,
          message: "validations.required.message",
        },
      },
    },
    {
      type: FieldTypes.text,
      name: "iban",
      label: "IBAN",
      validations: {
        required: {
          enabled: false,
          message: "",
        },
        text: {
          min: {
            value: 15,
            // t("validations.text.min.message")
            message: "validations.text.min.message",
          },
          max: {
            value: 34,
            // t("validations.text.max.message")
            message: "validations.text.max.message",
          },
        },
      },
    },
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  US: [
    PaymentInfoAutoFormFields["bankName"],
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.US
    ),
    PaymentInfoAutoFormFields["routingNumber"],
    PaymentInfoAutoFormFields["accountNumber"],
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  UY: [
    PaymentInfoAutoFormFields["bankName"],
    PaymentInfoAutoFormFields["accountNumber"],
    PaymentInfoAutoFormFields["branchCode"],
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.UY
    ),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
  ZA: [
    PaymentInfoAutoFormFields["bankName"],
    PaymentInfoAutoFormFields["accountNumber"],
    addOptions(
      PaymentInfoAutoFormFields["accountType"],
      BankAccountTypeOptions.ZA
    ),
    addConfig(PaymentInfoAutoFormFields["bankCertificate"], {
      label: "Bank Account Statement",
      message:
        "Must be dated within the last 3 months. Used to verify account ownership",
    }),
    PaymentInfoAutoFormFields["accountNickname"],
  ],
};
